import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import React from "react";

// Buttons inside a glass panel get tinted fills instead of their own blur,
// which would be redrawn for every button.
const press =
  "transition-[scale,background-color,color,opacity] duration-150 select-none active:scale-95 disabled:pointer-events-none disabled:opacity-40";
const plain =
  "bg-white/10 shadow-[inset_0_1px_0_rgb(255_255_255/0.15),inset_0_0_0_1px_rgb(255_255_255/0.06)]";
const accent =
  "bg-accent/90 text-slate-900 shadow-[inset_0_1px_0_rgb(255_255_255/0.55),inset_0_-2px_6px_rgb(0_0_0/0.12)]";

/**
 * The header of a panel the card grows into: ✕ on the left, the title in the
 * middle, and the panel's own round button on the right.
 */
export const PanelHeader: React.FC<{
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, closeLabel, onClose, children }) => (
  <div className="relative flex items-center justify-between">
    <CircleButton label={closeLabel} onClick={onClose}>
      <X className="size-6" strokeWidth={2.75} />
    </CircleButton>
    <h2 className="absolute inset-x-16 text-center text-[22px] font-bold">
      {title}
    </h2>
    {children}
  </div>
);

/** A round button for a panel header. */
export const CircleButton: React.FC<
  React.ComponentProps<"button"> & { label: string; tinted?: boolean }
> = ({ label, tinted = false, className, ...props }) => (
  <button
    type="button"
    className={cn(
      "relative z-1 grid size-[52px] shrink-0 place-items-center rounded-full",
      tinted ? accent : plain,
      press,
      className,
    )}
    aria-label={label}
    {...props}
  />
);

/** A selectable button: liquid green when selected. */
export const ChoiceButton: React.FC<
  React.ComponentProps<"button"> & {
    selected: boolean;
    role: "radio" | "checkbox";
  }
> = ({ selected, className, ...props }) => (
  <button
    type="button"
    className={cn(
      "min-w-0 flex-1 touch-manipulation rounded-2xl font-extrabold uppercase",
      selected ? accent : plain,
      press,
      className,
    )}
    aria-checked={selected}
    {...props}
  />
);

/** The big pill button at the bottom of a panel. */
export const PillButton: React.FC<
  React.ComponentProps<"button"> & { tinted?: boolean }
> = ({ tinted = true, className, ...props }) => (
  <button
    type="button"
    className={cn(
      "flex min-h-16 w-full items-center justify-center gap-3 rounded-full text-2xl font-extrabold uppercase",
      tinted ? accent : plain,
      press,
      className,
    )}
    {...props}
  />
);

/** A yes/no question before doing something that can't be undone, in the
 * panels' dark glass. */
export const ConfirmDialog: React.FC<{
  open: boolean;
  title: string;
  message: string;
  confirm: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, title, message, confirm, onConfirm, onCancel }) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
    <DialogContent
      showCloseButton={false}
      className="max-w-[340px] gap-6 rounded-[32px] border-white/10 bg-slate-800/80 p-6 text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.18),0_8px_24px_rgb(0_0_0/0.35)] backdrop-blur-xl backdrop-saturate-150 sm:max-w-[340px]"
    >
      <DialogHeader className="text-center sm:text-center">
        <DialogTitle className="text-2xl leading-tight font-extrabold">
          {title}
        </DialogTitle>
        <DialogDescription className="text-base text-white/70">
          {message}
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-2">
        <PillButton className="min-h-14 text-xl" onClick={onConfirm}>
          {confirm}
        </PillButton>
        <PillButton
          tinted={false}
          className="min-h-14 text-xl"
          onClick={onCancel}
        >
          Cancel
        </PillButton>
      </div>
    </DialogContent>
  </Dialog>
);
